import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../api";
import { LineChart, Line, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis } from "recharts";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin → User Detail.
 *
 * iter39 Group B. Drill-down view for a single user — profile, activity
 * histogram, top video sources, payments, bonuses, referrals.
 * Bundle endpoint `/admin/users/{id}/detail` returns everything in one
 * call so this page is render-then-display (no spinner per-panel).
 */
export default function UserDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get(`/admin/users/${userId}/detail`)
      .then(({ data }) => { if (!cancel) setData(data); })
      .catch((e) => { if (!cancel) setError(formatApiError(e)); });
    return () => { cancel = true; };
  }, [userId]);

  if (error) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="text-red-400 text-sm">{error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="text-muted-foreground font-mono text-sm">Loading…</div>
      </div>
    );
  }

  const { profile, attribution, activity, ltv_usd, payments,
          bonus_transactions, referrals_brought } = data;

  return (
    <div className="space-y-6" data-testid="user-detail-page">
      <BackLink />

      {/* ── Header: identity + plan + actions ─────────────────────── */}
      <div className="border border-border p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl">
              {profile.first_name || "—"} {profile.last_name || ""}
              {profile.is_blocked && (
                <span className="ml-3 text-xs font-mono uppercase text-red-400 border border-red-500/40 px-2 py-0.5">
                  blocked
                </span>
              )}
            </h1>
            <div className="text-xs font-mono text-muted-foreground mt-1">
              @{profile.username || "—"} · {profile.telegram_user_id} · {(profile.lang || "—").toUpperCase()}
            </div>
            <div className="text-xs font-mono text-muted-foreground mt-1">
              joined {fmtDateTime(profile.created_at)} · last seen {fmtDateTime(profile.last_seen_at)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <PlanBadge plan={profile.plan} />
            {profile.subscription_expires_at && (
              <div className="text-[10px] font-mono text-muted-foreground">
                Pro until {fmtDateTime(profile.subscription_expires_at)}
              </div>
            )}
            {profile.ton_pro_expires_at && (
              <div className="text-[10px] font-mono text-muted-foreground">
                TON-pro until {fmtDateTime(profile.ton_pro_expires_at)}
              </div>
            )}
          </div>
        </div>

        {/* Attribution row */}
        <div className="mt-4 pt-4 border-t border-border flex items-center gap-6 flex-wrap text-xs font-mono">
          <div>
            <span className="text-muted-foreground uppercase tracking-[0.16em]">Source:</span>{" "}
            {attribution.affiliate ? (
              <span className="text-cyan-400">
                affiliate · <Link to="/admin/affiliates" className="underline">{attribution.affiliate.display_name || attribution.affiliate.email}</Link>
                {attribution.affiliate.default_code && ` (${attribution.affiliate.default_code})`}
              </span>
            ) : attribution.referrer ? (
              <span className="text-amber-400">
                referral · <Link to={`/admin/users/${attribution.referrer.user_id}`} className="underline">
                  @{attribution.referrer.username || attribution.referrer.first_name || attribution.referrer.telegram_user_id}
                </Link>
              </span>
            ) : (
              <span className="text-muted-foreground">direct</span>
            )}
          </div>
          {profile.referral_code && (
            <div>
              <span className="text-muted-foreground uppercase tracking-[0.16em]">Ref code:</span>{" "}
              <span className="text-foreground">{profile.referral_code}</span>
            </div>
          )}
          {profile.ton_wallet_address && (
            <div className="truncate max-w-[300px]">
              <span className="text-muted-foreground uppercase tracking-[0.16em]">Wallet:</span>{" "}
              <span className="text-foreground">{profile.ton_wallet_address}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Top stats ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border">
        <Stat label="Jobs total" value={activity.jobs_total} testId="ud-jobs-total" />
        <Stat label="LTV" value={ltv_usd > 0 ? `$${ltv_usd.toFixed(2)}` : "$0"} tone={ltv_usd > 0 ? "success" : null} testId="ud-ltv" />
        <Stat label="Refine uses" value={activity.refine_count} testId="ud-refine" />
        <Stat label="Motion crops" value={activity.motion_count} testId="ud-motion" />
        <Stat label="Bonus stars" value={`${profile.bonus_balance_stars ?? 0} ⭐`} testId="ud-bonus" />
      </div>

      {/* ── 90-day activity histogram ─────────────────────────── */}
      <Panel title="Activity · 90 days" testId="ud-activity-chart">
        {activity.by_day.length === 0 ? (
          <Empty>No jobs in the last 90 days.</Empty>
        ) : (
          <div className="h-32">
            <ResponsiveContainer>
              <BarChart data={activity.by_day}>
                <XAxis dataKey="day" hide />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12, fontFamily: "JetBrains Mono, monospace" }}
                  labelFormatter={(d) => d}
                  formatter={(v) => [v, "jobs"]}
                />
                <Bar dataKey="count" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>

      {/* ── Job-type & source breakdowns side-by-side ─────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border">
        <Panel title="By job type">
          <BreakdownList items={Object.entries(activity.by_type).map(([k, v]) => ({ label: k, count: v }))} total={activity.jobs_total} />
        </Panel>
        <Panel title="Top video sources">
          <BreakdownList items={activity.top_sources.slice(0, 8).map((r) => ({ label: r.source, count: r.count }))} total={activity.jobs_total} />
        </Panel>
      </div>

      {/* ── Payments ─────────────────────────────────────────── */}
      <Panel title={`Payments · ${payments.length}`} testId="ud-payments">
        {payments.length === 0 ? (
          <Empty>No payments yet.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left py-2">When</th>
                <th className="text-left">Method</th>
                <th className="text-right">Native</th>
                <th className="text-right">USD</th>
                <th className="text-left pl-4">Note</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, i) => (
                <tr key={i} className="border-b border-border last:border-b-0">
                  <td className="py-2 font-mono text-xs text-muted-foreground">{fmtDateTime(p.at)}</td>
                  <td className="font-mono text-xs uppercase">{p.method}</td>
                  <td className="text-right font-mono tabular-nums">{p.amount_native} {p.currency_native === "XTR" ? "⭐" : p.currency_native}</td>
                  <td className="text-right font-mono tabular-nums text-emerald-400">${p.amount_usd.toFixed(2)}</td>
                  <td className="pl-4 text-xs text-muted-foreground">{p.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {/* ── Bonus transactions ──────────────────────────────── */}
      <Panel title={`Bonus history · ${bonus_transactions.length}`} testId="ud-bonus-history">
        {bonus_transactions.length === 0 ? (
          <Empty>No bonus activity.</Empty>
        ) : (
          <ul className="divide-y divide-border">
            {bonus_transactions.map((b, i) => (
              <li key={i} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="font-mono text-xs uppercase text-muted-foreground tracking-[0.14em]">{b.txn_type}</div>
                  {b.note && <div className="text-xs text-muted-foreground mt-0.5">{b.note}</div>}
                </div>
                <div className="text-right">
                  <div className={`font-mono tabular-nums ${(b.amount ?? 0) > 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {(b.amount ?? 0) > 0 ? "+" : ""}{b.amount} ⭐
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">{fmtDateTime(b.created_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* ── Referrals brought in ────────────────────────────── */}
      <Panel title={`Referrals brought · ${referrals_brought.length}`} testId="ud-referrals">
        {referrals_brought.length === 0 ? (
          <Empty>No referrals yet.</Empty>
        ) : (
          <ul className="divide-y divide-border">
            {referrals_brought.map((r) => (
              <li key={r.id} className="py-2 flex items-center justify-between">
                <div>
                  <Link to={`/admin/users/${r.id}`} className="text-sm hover:underline">
                    {r.first_name || "—"} {r.last_name || ""}
                  </Link>
                  <div className="text-xs font-mono text-muted-foreground">@{r.username || "—"} · {r.telegram_user_id}</div>
                </div>
                <div className="flex items-center gap-3">
                  <PlanBadge plan={r.plan} />
                  {r.has_paid_subscription_before && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-emerald-400 border border-emerald-500/40 px-1.5">
                      paid
                    </span>
                  )}
                  <div className="font-mono text-[10px] text-muted-foreground">joined {fmtDateTime(r.created_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/users"
      className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground transition-colors"
      data-testid="ud-back-link"
    >
      <ArrowLeft className="w-3.5 h-3.5" />
      Back to users
    </Link>
  );
}

function PlanBadge({ plan }) {
  const colors = {
    free: "border-border text-muted-foreground",
    pro: "border-emerald-500/40 text-emerald-400",
    pro_plus: "border-violet-500/40 text-violet-400",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border ${colors[plan] || colors.free}`}>
      {plan}
    </span>
  );
}

function Stat({ label, value, tone, testId }) {
  return (
    <div className="bg-background p-4" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl ${tone === "success" ? "text-emerald-400" : ""}`}>{value}</div>
    </div>
  );
}

function Panel({ title, children, testId }) {
  return (
    <div className="border border-border bg-background p-4" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">{title}</div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-xs text-muted-foreground font-mono py-2">{children}</div>;
}

function BreakdownList({ items, total }) {
  if (!items.length) return <Empty>No data.</Empty>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <ul className="space-y-2">
      {items.map((it) => {
        const pct = total ? Math.round((it.count / total) * 100) : 0;
        const widthPct = (it.count / max) * 100;
        return (
          <li key={it.label}>
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="uppercase tracking-[0.12em]">{it.label}</span>
              <span className="text-muted-foreground tabular-nums">{it.count} · {pct}%</span>
            </div>
            <div className="h-1 bg-border mt-1">
              <div className="h-full bg-foreground/60" style={{ width: `${widthPct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
